import * as vp from 'vopay';
import { FunctionComponent, useState, useEffect } from 'react';
import { addDays, format, startOfDay } from 'date-fns';
import styles from '../AccountingSystemOpen.module.scss';
import BulkItem from './BulkItem';
import { useAccountingSoftware } from '#shared/components/AccountingSoftware/AccountingSoftwareContext';
import BankingUpdateMiniDialogue from '../BankingUpdateMiniDialogue/BankingUpdateMiniDialogue';
import { PaymentUpdate } from '../types';
import {
  getAvailablePaymentMethodsDropdown,
  updateCustomerData,
  roundCurrency,
  IS_SCHEDULING_SUPPORTED_ON_ASP,
} from '../utils';
import AccountingSystemScheduleSidebar from '../AccountingSystemScheduleDialogue/AccountingSystemScheduleSideBar';
import useFetchAspInvoiceCreditNotes from '#shared/components/AccountingSoftware/hooks/useFetchAspInvoiceCreditNotes';
import useFetchAspBillCreditNotes from '#shared/components/AccountingSoftware/hooks/useFetchAspBillCreditNotes';
import { getAspRowExtras } from '#shared/components/AccountingSoftware/aspAccountingAdapter';
import usePersistAspPaymentMethod from '#shared/components/AccountingSoftware/usePersistAspPaymentMethod';
import { useBlockingLoader } from '#shared/providers/Loader';
import { useTranslation } from 'react-i18next';

import { Button } from '#shared/components/UI';

interface BankingUpdateDialogueProps {
  isVisible: boolean;
  type: 'Payable' | 'Receivable';
  listOfSelected: vp.TemporaryBillsOrInvoicesRails[];
  financialInstitutions: {
    [key: string]: vp.FinancialInstitution;
  };
  close: (selectedList: vp.TemporaryBillsOrInvoicesRails[], isSaved: boolean) => void;
  onSchedule: (selectedList: vp.TemporaryBillsOrInvoicesRails[]) => void;
  onScheduleDate: (selectedList: vp.TemporaryBillsOrInvoicesRails[], date: string) => void;
  onCollect: (selectedList: vp.TemporaryBillsOrInvoicesRails[]) => void;
}
type FC = FunctionComponent<BankingUpdateDialogueProps>;
const { Modal, SubTitle, Box, DropDown, Icon } = vp;

const BulkPaymentDialogue: FC = ({
  isVisible,
  type,
  close,
  listOfSelected,
  onCollect,
  financialInstitutions,
  onSchedule,
  onScheduleDate,
}: BankingUpdateDialogueProps) => {
  const [copySelectedList, setCopySelectedList] = useState<vp.TemporaryBillsOrInvoicesRails[]>([]);
  const [showUpdateBanking, setShowUpdateBanking] = useState(false);
  const [updateType, setUpdateType] = useState<'Edit' | 'Add'>('Edit');
  const [originalValues, setOriginalValues] = useState<vp.TemporaryBillsOrInvoicesRails>();
  const [showCalendar, setShowCalendar] = useState(false);
  const blockingLoader = useBlockingLoader();
  const { persistPaymentMethod } = usePersistAspPaymentMethod();

  const [bankingDefault, setBankingDefault] = useState<PaymentUpdate>({
    PaymentMethod: '',
    InteracMethod: '',
  });

  const [isOptionVisible, setIsOptionVisible] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const { fetchAspInvoiceCreditNotes } = useFetchAspInvoiceCreditNotes();
  const { fetchAspBillCreditNotes } = useFetchAspBillCreditNotes();
  const [creditNotes, setCreditNotes] = useState<vp.CreditNote[]>([]);
  const [appliedCreditNotes, setAppliedCreditNotes] = useState('');
  const [totalCreditsApplied, setTotalCreditsApplied] = useState(0);
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.AccountingSystem.OpenTab.Dialogs.BulkPayment' });
  const { t: tUtils } = useTranslation('translations', { keyPrefix: 'Shared.AccountingSystem.OpenTab.Utils' });

  useEffect(() => {
    const fetchData = async () => {
      if (isVisible) {
        blockingLoader.show();

        const uniqueContacts = new Map<number, number>();

        listOfSelected.forEach((item) => {
          const extras = getAspRowExtras(item);

          if (extras && extras.AspContactID !== null) {
            uniqueContacts.set(extras.AspContactID, extras.AspConnectionID);
          }
        });

        const creditsResponse = await Promise.all(
          [...uniqueContacts.entries()].map(async ([ContactID, ConnectionID]) => {
            try {
              return type === 'Receivable'
                ? await fetchAspInvoiceCreditNotes({ ConnectionID, ContactID })
                : await fetchAspBillCreditNotes({ ConnectionID, ContactID });
            } catch {
              // Credit notes are optional context — a failed contact just
              // contributes none, same as the legacy non-Success branch.
              return null;
            }
          }),
        );

        const aggregatedCreditNotes = creditsResponse
          .filter((response) => response !== null)
          .flatMap((response) => response.CreditNotes);

        setCreditNotes(aggregatedCreditNotes);

        blockingLoader.hide();
      } else {
        setCreditNotes([]);
      }
    };

    fetchData();
  }, [isVisible]);

  const {
    account: { Country },
  } = useAccountingSoftware();

  const currency = Country === 'CA' ? 'CAD' : 'USD';

  useEffect(() => {
    const copyOfSelected = [...listOfSelected];

    setCopySelectedList(copyOfSelected);
  }, [listOfSelected, isVisible]);

  useEffect(() => {
    if (isVisible) {
      setIsSaved(false);
    }
  }, [isVisible]);

  useEffect(() => {
    const appliedCreditNotesList = copySelectedList
      .filter((selected) => selected.isSelected)
      .flatMap((selected) => selected.creditNotes?.split(','))
      .filter((selected) => selected);

    setAppliedCreditNotes(appliedCreditNotesList.join(', '));

    const appliedCreditNotesCalc = creditNotes.filter((creditNote) => appliedCreditNotesList.includes(creditNote.id));

    const totalCredits = appliedCreditNotesCalc.reduce((acc, item) => roundCurrency(acc + item.remainingCredit), 0);

    setTotalCreditsApplied(totalCredits);
  }, [copySelectedList]);

  const onSaveItem = (item: vp.TemporaryBillsOrInvoicesRails) => {
    const newList: vp.TemporaryBillsOrInvoicesRails[] = copySelectedList.map(
      (selectedListItem: vp.TemporaryBillsOrInvoicesRails) => {
        if (selectedListItem.id === item.id) {
          return item;
        }

        return selectedListItem;
      },
    );

    setCopySelectedList(newList);
  };

  // refactor this function to be used here and bulkItem
  const SaveBank = async (values: PaymentUpdate) => {
    if (!originalValues) return;

    // On asp rows the method must live on the contact's client account before
    // collecting — a failed save keeps the dialog open.
    if (!(await persistPaymentMethod(originalValues, values))) return;

    const updatedData = updateCustomerData(values, originalValues, Country, type, originalValues.PaidAmount, '');
    onSaveItem(updatedData);
    setShowUpdateBanking(false);
    setIsSaved(true);
  };

  return (
    <Modal isVisible={isVisible} enableBgClick={false}>
      <div
        className={[
          styles.dialogueBulk,
          showUpdateBanking || showCalendar ? styles.disabledOverview : '',
          showUpdateBanking ? styles.sidePanelOpen : '',
        ].join(' ')}
      >
        <AccountingSystemScheduleSidebar
          isVisible={showCalendar}
          defaultDate={String(startOfDay(addDays(new Date(), 1)).getTime())}
          onSchedule={(scheduleDate) => {
            onScheduleDate(
              copySelectedList.filter((item) => item.isSelected === true),
              format(new Date(Number(scheduleDate)), 'yyyy-MM-dd'),
            );

            setShowCalendar(false);
          }}
          close={() => setShowCalendar(false)}
        />

        <BankingUpdateMiniDialogue
          type={type}
          updateType={updateType}
          isVisible={showUpdateBanking}
          financialInstitutions={financialInstitutions}
          originalValue={originalValues}
          defaultValues={bankingDefault}
          close={() => setShowUpdateBanking(false)}
          onSave={(values) => {
            SaveBank(values);
          }}
        />

        <Icon
          className={styles.closeButton}
          icon="cross"
          height="12px"
          width="12px"
          onClick={() => {
            close(copySelectedList, isSaved);
          }}
        />

        <Box pb="lg" base="8" flx="col">
          <SubTitle
            center
            subTitle={`${type === 'Receivable' ? t('collectTitle', { defaultValue: 'Collect' }) : t('payTitle', { defaultValue: 'Pay' })} ${type}`}
          />

          <Box gap="lg" pt="md" base="8" flx="col">
            <Box jc="center" height="auto">
              {type === 'Receivable' && (
                <div className={styles.dialogueParagraph} style={{ textAlign: 'center' }}>
                  {t('receivableDescription', {
                    defaultValue: 'Account information is saved once the collection request is sent.',
                  })}
                </div>
              )}

              {type === 'Payable' && (
                <div className={styles.dialogueParagraph} style={{ textAlign: 'center' }}>
                  {t('payableDescription', { defaultValue: 'Account information is saved once the Payment is sent.' })}
                </div>
              )}
            </Box>

            <Box>
              <div className={styles.bankList}>
                {copySelectedList.map((selectedItem) => (
                  <BulkItem
                    paymentMethods={getAvailablePaymentMethodsDropdown(
                      selectedItem,
                      Country,
                      type,
                      (newPaymentMethod) => {
                        setUpdateType('Edit');
                        setOriginalValues(selectedItem);

                        const currentValues: PaymentUpdate = {
                          PaymentMethod: newPaymentMethod,
                          FinancialInstitutionNumber: selectedItem?.Customer?.BankAccount?.FinancialInstitutionNumber,
                          BankTransitNumber: selectedItem?.Customer?.BankAccount?.BranchTransitNumber,
                          AccountNumber: selectedItem?.Customer?.BankAccount?.AccountNumber,
                          Email: selectedItem?.Customer?.Email || selectedItem?.Customer?.Interac?.EmailAddress,
                          Phone: selectedItem?.Customer?.Phone || selectedItem?.Customer?.Interac?.PhoneNumber || '',
                          Question: selectedItem?.Customer?.Interac?.Question,
                          Answer: selectedItem?.Customer?.Interac?.Answer,
                          PhoneNumber: selectedItem?.Customer?.BankAccount?.PhoneNumber,
                          City: selectedItem?.Customer?.BankAccount?.City,
                          Address: selectedItem?.Customer?.BankAccount?.Address,
                          State: selectedItem?.Customer?.BankAccount?.State,
                          ZipCode: selectedItem?.Customer?.BankAccount?.ZipCode,
                          InteracMethod: selectedItem?.Customer?.Interac?.EmailAddress
                            ? 'Email Address'
                            : selectedItem?.Customer?.Interac?.PhoneNumber
                              ? 'Phone Number'
                              : 'Email Address',
                          TokenDebit: selectedItem?.Customer?.DebitCard?.Token,
                          TokenCredit: selectedItem?.Customer?.CreditCard?.Token,
                          Language: selectedItem?.Customer?.Interac?.Language || 'en',
                        };

                        setBankingDefault(currentValues);
                        setShowUpdateBanking(true);
                      },
                      tUtils,
                    )}
                    defaultValues={selectedItem}
                    creditNotes={creditNotes.filter(
                      (creditNote) =>
                        (creditNote.customerRef?.id && creditNote.customerRef.id === selectedItem.customerRef?.id) ||
                        (creditNote.vendorRef?.id && creditNote.vendorRef.id === selectedItem.vendorRef?.id),
                    )}
                    appliedCreditNotes={appliedCreditNotes.split(',')}
                    id={selectedItem.id}
                    onSave={onSaveItem}
                    onUpdate={(innerUpdateType, innerOriginalValues, innerBankingDefault) => {
                      setUpdateType(innerUpdateType);
                      setOriginalValues(innerOriginalValues);
                      setBankingDefault(innerBankingDefault);
                      setShowUpdateBanking(true);
                    }}
                    type={type}
                  />
                ))}
              </div>
            </Box>

            <div className={styles.greyCard}>
              <div className={styles.cardBulkTotalContainer}>
                <div className={styles.cardBulkTotalItems}>
                  <div className={styles.cardBulkLabel}>{t('totalSelected', { defaultValue: 'Total Selected:' })}</div>

                  <div className={styles.cardBulkValue}>
                    {currency}{' '}
                    {vp.formattedCurrency(
                      String(
                        copySelectedList
                          .filter((item) => item.isSelected)
                          .reduce(
                            (remainingAmount, item) =>
                              remainingAmount +
                              Number(vp.sanitizeToNumeric(String(item.amountDue ? item.amountDue : item.totalAmount))),
                            0,
                          ),
                      ),
                    )}
                  </div>
                </div>

                <div className={styles.cardBulkTotalItems}>
                  <div className={styles.cardBulkLabel}>
                    {type === 'Receivable'
                      ? t('collectViaPaymentMethod', { defaultValue: 'Collect via Payment Method' })
                      : t('payViaPaymentMethod', { defaultValue: 'Pay via Payment Method' })}
                  </div>

                  <div className={styles.cardBulkValue}>
                    {currency}{' '}
                    {vp.formattedCurrency(
                      String(
                        copySelectedList
                          .filter((item) => item.isSelected)
                          .reduce((remainingAmount, item) => remainingAmount + Number(item.PaidAmount), 0),
                      ),
                    )}
                  </div>
                </div>

                <div className={styles.cardBulkTotalItems}>
                  <div className={styles.cardBulkLabel}>{t('creditsApplied', { defaultValue: 'Credits Applied' })}</div>

                  <div className={styles.cardBulkValue}>
                    {currency} {vp.formattedCurrency(String(totalCreditsApplied))}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.dialogueInfo}>
              {t('addMissingPaymentMethodInfo', {
                defaultValue: 'Please add missing payment method(s) for invoice(s) as required to proceed.',
              })}
            </div>
          </Box>
        </Box>

        {/* Right-aligned while the schedule dropdown is hidden — space-between
            would strand the lone action button on the left. */}
        <Box jc={IS_SCHEDULING_SUPPORTED_ON_ASP ? 'sb' : 'fe'} gap="sm" flx="row">
          {/* The schedule dropdown (due date / custom date) is hidden while
              scheduling is unsupported on asp — see IS_SCHEDULING_SUPPORTED_ON_ASP. */}
          {IS_SCHEDULING_SUPPORTED_ON_ASP && (
            <Box flx="col" jc="fe">
              <Box flx="row" jc="fs">
                <Button
                  id="export-transactions"
                  colorScheme="secondary"
                  size="standard"
                  iconRight="keyboard_arrow_down"
                  onClick={() => {
                    setIsOptionVisible(true);
                  }}
                  disabled={
                    copySelectedList.some(
                      (item) =>
                        item.hasBankAccount === false &&
                        item.hasCreditCard === false &&
                        item.hasDebitCard === false &&
                        item.hasInterac === false &&
                        item.paymentMethodUser !== 'eLinx' &&
                        item.isSelected === true,
                    ) ||
                    copySelectedList.filter((item) => item.isSelected === true).length === 0 ||
                    copySelectedList.some((item) => item.isSelected === true && item.paymentMethodUser === 'eLinx')
                  }
                >
                  <Box pe="sm" ai="center">
                    {type === 'Receivable'
                      ? t('ScheduleDropdown.receivableLabel', { defaultValue: 'Schedule Collection' })
                      : t('ScheduleDropdown.payableLabel', { defaultValue: 'Schedule Payment' })}
                  </Box>
                </Button>

                <div />
              </Box>

              <div style={{ width: '215px' }} />

              <DropDown isMenuVisible={isOptionVisible} onClick={setIsOptionVisible} header={false}>
                <vp.Button
                  id="collect-on-due-date-button"
                  label={
                    type === 'Receivable'
                      ? t('ScheduleDropdown.collectOnDueDate', { defaultValue: 'Collect  on Due Date' })
                      : t('ScheduleDropdown.payOnDueDate', { defaultValue: 'Pay on Due Date' })
                  }
                  color="none"
                  background="transparent"
                  textSize={14}
                  fontUnit="px"
                  width="100%"
                  noShadow
                  textAlign="left"
                  iconLocation="left"
                  height="38px"
                  icon="bill"
                  iconWidth="27px"
                  iconHeight="24px"
                  justifyLabel="fs"
                  onClick={() => {
                    setIsOptionVisible(false);
                    onSchedule(copySelectedList.filter((item) => item.isSelected === true));
                  }}
                />

                <vp.Button
                  id="custom-date-button"
                  label={t('ScheduleDropdown.customDate', { defaultValue: 'Custom Date' })}
                  color="none"
                  background="transparent"
                  textSize={14}
                  fontUnit="px"
                  width="100%"
                  noShadow
                  textAlign="left"
                  iconLocation="left"
                  icon="calendarSchedule"
                  height="38px"
                  iconWidth="27px"
                  iconHeight="24px"
                  justifyLabel="fs"
                  onClick={() => {
                    setIsOptionVisible(false);
                    setShowCalendar(true);
                  }}
                />
              </DropDown>
            </Box>
          )}

          <Button
            id="save-and-collect"
            onClick={() => {
              onCollect(copySelectedList.filter((item) => item.isSelected === true));
            }}
            colorScheme="primary"
            size="standard"
            disabled={
              copySelectedList.some(
                (item) =>
                  item.hasBankAccount === false &&
                  item.hasCreditCard === false &&
                  item.hasDebitCard === false &&
                  item.hasInterac === false &&
                  item.paymentMethodUser !== 'eLinx' &&
                  item.isSelected === true,
              ) || copySelectedList.filter((item) => item.isSelected === true).length === 0
            }
          >
            {type === 'Receivable'
              ? t('saveAndCollect', { defaultValue: 'Save & Collect Now' })
              : t('saveAndPay', { defaultValue: 'Save & Pay Now' })}
          </Button>
        </Box>

        <Box pt="sm" flx="col">
          <div className={styles.disabledMessage}>
            {t('footerMessage', {
              defaultValue: 'Credits are applied once you click ‘{{type}}’',
              type: type === 'Receivable' ? 'Collect' : 'Pay',
            })}
          </div>
        </Box>
      </div>
    </Modal>
  );
};

export default BulkPaymentDialogue;
